<template>
  <div class="logo-block">
    <div class="block-label-row">
      <label class="block-label">Business logo</label>
      <v-tooltip location="top" max-width="300">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            icon="mdi-help-circle-outline"
            size="small"
            class="field-help-icon"
            aria-label="Logo help"
          />
        </template>
        <span>{{ hint }}</span>
      </v-tooltip>
    </div>
    <input
      ref="logoFileInput"
      type="file"
      :accept="acceptAttr"
      class="visually-hidden"
      aria-label="Upload business logo"
      :disabled="readonly || checking"
      @change="onLogoFileSelected"
    />
    <div
      v-if="!logoPreviewUrl"
      class="logo-upload-empty"
      :class="{ 'logo-upload-empty--readonly': readonly }"
      role="button"
      :tabindex="readonly ? -1 : 0"
      :aria-disabled="readonly ? 'true' : 'false'"
      :aria-busy="checking ? 'true' : 'false'"
      @click="readonly || triggerFilePicker()"
      @keydown.enter.prevent="readonly || triggerFilePicker()"
      @keydown.space.prevent="readonly || triggerFilePicker()"
    >
      <v-progress-circular
        v-if="checking"
        indeterminate
        color="primary"
        :size="24"
        :width="2"
        class="logo-upload-icon"
      />
      <v-icon v-else class="logo-upload-icon" color="green-darken-2" size="28">mdi-cloud-upload-outline</v-icon>
      <div class="logo-upload-labels">
        <span class="logo-upload-text">{{ checking ? "Checking your logo…" : "Click to upload logo" }}</span>
        <span class="logo-upload-hint">{{ requirements }}</span>
      </div>
    </div>
    <div v-else class="logo-upload-filled">
      <img :src="logoPreviewUrl" class="logo-preview-img" alt="Logo preview" />
      <div class="logo-upload-meta">
        <span class="logo-upload-filename">{{ logoFileName || "Current logo" }}</span>
        <div v-if="!readonly" class="logo-upload-actions">
          <button type="button" class="logo-action-btn" :disabled="checking" @click="triggerFilePicker">
            Replace
          </button>
          <button type="button" class="logo-action-btn danger" :disabled="checking" @click="removeLogo">
            Remove
          </button>
        </div>
      </div>
    </div>
    <p v-if="logoError || validationError" class="logo-error-text" role="alert">
      {{ logoError || validationError }}
    </p>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { track } from "@/analytics.js";
import {
  MAX_LOGO_SIZE,
  LOGO_ACCEPT_ATTR,
  LOGO_REQUIREMENTS_SHORT,
  LOGO_REQUIREMENTS_HINT,
  needsPngConversion,
  validateLogoFile,
  validateLogoDimensions,
} from "@/utils/logoRequirements.js";

// Re-exported for callers that only need the size cap.
export { MAX_LOGO_SIZE };

// Decodes the image to read its pixel size. Rejects when the browser can't
// decode it (corrupt file, unsupported format).
const readImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image could not be decoded"));
    };
    img.src = url;
  });

// Apple Wallet needs a real PNG, and the file is stored as `${id}.png`
// whatever it was, so JPGs are re-encoded here before they leave the browser.
const convertToPng = (img, originalName) =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx || typeof canvas.toBlob !== "function") {
      reject(new Error("Canvas not available"));
      return;
    }
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("PNG conversion failed"));
        return;
      }
      const baseName = String(originalName || "logo").replace(/\.[^.]+$/, "");
      resolve(new File([blob], `${baseName}.png`, { type: "image/png" }));
    }, "image/png");
  });

// Logo picker with preview, replace/remove and validation against the
// Apple and Google Wallet requirements (see src/utils/logoRequirements.js).
// Shared by the registration stepper and the dashboard branding editor.
export default {
  name: "LogoUploader",
  emits: ["update:logoFile", "logo-error"],
  props: {
    // Optional remote logo URL (for the dashboard editor - registration
    // path uses the in-memory preview generated from the selected file).
    initialLogoUrl: {
      type: String,
      default: "",
    },
    // Optional file name to seed the displayed filename. Used by the
    // registration stepper to keep the original filename visible when the
    // user navigates back after uploading.
    initialLogoFileName: {
      type: String,
      default: "",
    },
    // `:disabled` on the file input because there's no readonly affordance
    // for that primitive (see BrandingEditor's `readonly` prop).
    readonly: {
      type: Boolean,
      default: false,
    },
    // External validation error (e.g. "please upload a logo" when the
    // parent's continue check fails). Internal upload errors (file too
    // large, wrong size, etc.) take priority since they're more specific.
    validationError: {
      type: String,
      default: "",
    },
  },
  setup(props, { emit }) {
    const logoFileInput = ref(null);
    const logoFile = ref(null);
    const logoPreviewUrl = ref(props.initialLogoUrl || "");
    const logoFileName = ref(props.initialLogoFileName || "");
    const logoError = ref("");
    // True while a picked file is being decoded, measured and converted.
    const checking = ref(false);
    let createdObjectUrl = "";

    const revokeUrl = () => {
      if (createdObjectUrl) {
        URL.revokeObjectURL(createdObjectUrl);
        createdObjectUrl = "";
      }
    };

    const triggerFilePicker = () => {
      if (checking.value) return;
      if (logoFileInput.value) logoFileInput.value.click();
    };

    const resetInput = () => {
      if (logoFileInput.value) logoFileInput.value.value = "";
    };

    const reject = (message, reason, file, dims = {}) => {
      logoError.value = message;
      track("branding_logo_rejected", {
        reason,
        file_size: file?.size ?? null,
        file_type: file?.type || "unknown",
        width: dims.width ?? null,
        height: dims.height ?? null,
      });
      if (reason === "too_large") {
        track("branding_logo_too_large", {
          file_size: file.size,
          max_size: MAX_LOGO_SIZE,
        });
      }
      emit("logo-error", message);
      resetInput();
    };

    const onLogoFileSelected = async (event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      logoError.value = "";

      const fileError = validateLogoFile(file);
      if (fileError) {
        const reason = file.size > MAX_LOGO_SIZE ? "too_large" : "wrong_type";
        reject(fileError, reason, file);
        return;
      }

      checking.value = true;
      try {
        let img;
        try {
          img = await readImage(file);
        } catch (_) {
          reject(validateLogoDimensions({ width: 0, height: 0 }), "unreadable", file);
          return;
        }
        const dims = { width: img.naturalWidth, height: img.naturalHeight };
        const dimsError = validateLogoDimensions(dims);
        if (dimsError) {
          const reason =
            dims.width < 660 || dims.height < 660 ? "too_small" : "not_square";
          reject(dimsError, reason, file, dims);
          return;
        }

        let accepted = file;
        if (needsPngConversion(file)) {
          try {
            accepted = await convertToPng(img, file.name);
          } catch (_) {
            reject(
              "We couldn't convert that image to PNG. Please upload a PNG instead.",
              "conversion_failed",
              file,
              dims,
            );
            return;
          }
        }

        const isReplace = !!logoFile.value || !!logoPreviewUrl.value;
        revokeUrl();
        logoFile.value = accepted;
        logoFileName.value = file.name;
        createdObjectUrl = URL.createObjectURL(accepted);
        logoPreviewUrl.value = createdObjectUrl;
        track("branding_logo_uploaded", {
          file_size: accepted.size,
          file_type: file.type || "unknown",
          converted_to_png: accepted !== file,
          width: dims.width,
          height: dims.height,
          is_replace: isReplace,
        });
        emit("update:logoFile", accepted);
      } finally {
        checking.value = false;
      }
    };

    const removeLogo = () => {
      revokeUrl();
      logoFile.value = null;
      logoFileName.value = "";
      logoPreviewUrl.value = "";
      logoError.value = "";
      resetInput();
      track("branding_logo_removed", {});
      emit("update:logoFile", null);
    };

    // If the parent provides an initialLogoUrl (dashboard editing path),
    // surface it as the current preview without forcing a re-upload.
    watch(
      () => props.initialLogoUrl,
      (next) => {
        if (next && !logoFile.value) {
          logoPreviewUrl.value = next;
        }
      }
    );

    onBeforeUnmount(revokeUrl);

    return {
      logoFileInput,
      logoPreviewUrl: computed(() => logoPreviewUrl.value),
      logoFileName,
      logoError,
      checking,
      hint: LOGO_REQUIREMENTS_HINT,
      requirements: LOGO_REQUIREMENTS_SHORT,
      acceptAttr: LOGO_ACCEPT_ATTR,
      triggerFilePicker,
      onLogoFileSelected,
      removeLogo,
    };
  },
};
</script>

<style scoped>
.logo-block {
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: var(--lulu-surface);
  border-radius: 12px;
  border: 1px solid var(--lulu-border);
}

.block-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
}

.block-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.field-help-icon {
  color: var(--lulu-text-muted);
  opacity: 0.7;
  cursor: help;
  transition: color 0.15s, opacity 0.15s;
}

.field-help-icon:hover {
  opacity: 1;
  color: var(--lulu-primary);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.logo-upload-empty {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 14px 16px;
  border: 1.5px dashed rgba(0, 109, 62, 0.35);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background: #fcfffd;
}

.logo-upload-empty:hover,
.logo-upload-empty:focus-visible {
  border-color: var(--lulu-primary);
  background-color: rgba(0, 109, 62, 0.03);
  outline: none;
}

.logo-upload-empty--readonly {
  cursor: default;
  opacity: 0.65;
}

.logo-upload-empty--readonly:hover {
  border-color: rgba(0, 109, 62, 0.35);
  background: #fcfffd;
}

.logo-upload-icon {
  color: var(--lulu-primary) !important;
  opacity: 1;
  flex-shrink: 0;
}

.logo-upload-labels {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.logo-upload-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
}

.logo-upload-hint {
  font-size: 12px;
  color: #4a5568;
}

.logo-upload-filled {
  display: flex;
  align-items: center;
  gap: 14px;
}

.logo-preview-img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid var(--lulu-border);
  flex-shrink: 0;
}

.logo-upload-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.logo-upload-filename {
  font-size: 12px;
  color: #4a5568;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.logo-upload-actions {
  display: flex;
  gap: 12px;
}

.logo-action-btn {
  font-size: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-weight: 600;
  color: var(--lulu-primary);
}

.logo-action-btn.danger {
  color: var(--lulu-danger);
}

.logo-action-btn:hover:not(:disabled) {
  text-decoration: underline;
}

.logo-action-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.logo-error-text {
  color: var(--lulu-danger-strong);
  font-size: 12px;
  margin-top: 8px;
  font-weight: 500;
}
</style>
